import type { AddToCart } from "../../../cbi-generator/generated/dto/AddToCart";
import type { CatalogItemInstanceDTOBase } from "../../../cbi-generator/generated/dto/CatalogItemInstanceDTOBase";
import type { CatalogItemDTOBase } from "../../../cbi-generator/generated/dto/CatalogItemDTOBase";
import type { CatalogStaff } from "../../../cbi-generator/generated/dto/CatalogStaff";
import { ItemType } from "../../../cbi-generator/generated/dto/ItemType";
import type { MembershipRef } from "../../../cbi-generator/generated/dto/MembershipRef";
import type { PersonRef } from "../../../cbi-generator/generated/dto/PersonRef";
import type { DonationPurchaseDetails } from "../../../cbi-generator/generated/dto/DonationPurchaseDetails";
import type { LineItemRef } from "../../../cbi-generator/generated/dto/LineItemRef";

export interface CatalogPurchaseInput {
  recipient: PersonRef | null;
  quantity: number;
  specifiedCents: number | undefined;
  cardNumber: string;
  balanceNumber: string;
  membership: MembershipRef | null;
  donation?: DonationPurchaseDetails | null;
  /** Reconfigure this cart line instead of adding a new one. */
  updateLine?: LineItemRef | null;
}

export type CatalogPurchaseItem = Omit<
  CatalogItemInstanceDTOBase,
  "itemIdTargetIds"
> & {
  readonly itemId: Pick<
    CatalogItemDTOBase,
    "assignId" | "itemType" | "itemName" | "itemDescription" | "itemSku"
  > & {
    readonly catalogId: Pick<CatalogStaff, "catalogName"> | null;
    readonly imageUrl?: string | null;
  };
};

export function requiresRecipient(type: ItemType | null) {
  return (
    type === ItemType.MEMBERSHIP ||
    type === ItemType.DAMAGE_WAIVER ||
    type === ItemType.GUEST_PRIVS
  );
}

export function canQuickAdd(
  item: CatalogPurchaseItem,
  recipient: PersonRef | null,
) {
  const type = item.itemId.itemType;
  return (
    type != null &&
    type !== ItemType.PAID_CLASS &&
    type !== ItemType.GUEST_PRIVS &&
    type !== ItemType.DONATION &&
    item.allowPriceOverride !== true &&
    item.priceCents != null &&
    (!requiresRecipient(type) || recipient != null)
  );
}

export function buildCatalogPurchase(
  item: CatalogPurchaseItem,
  input: CatalogPurchaseInput,
): AddToCart {
  const type = item.itemId.itemType;
  if (type === ItemType.PAID_CLASS)
    throw new Error("Enroll through the class page to purchase a class.");
  if (!type) throw new Error("This catalog item has no purchasable type.");
  if (requiresRecipient(type) && !input.recipient)
    throw new Error("Choose who this purchase is for.");
  if (
    input.recipient &&
    (!Number.isSafeInteger(input.recipient.personId) ||
      input.recipient.personId <= 0)
  )
    throw new Error("Choose a valid recipient.");
  if (!Number.isSafeInteger(input.quantity) || input.quantity < 1)
    throw new Error("Quantity must be a positive whole number.");
  if (type === ItemType.PRODUCT) {
    if (input.quantity < (item.minQuantity ?? 1))
      throw new Error(`Minimum quantity is ${item.minQuantity}.`);
    if (item.maxQuantity != null && input.quantity > item.maxQuantity)
      throw new Error(`Maximum quantity is ${item.maxQuantity}.`);
  }
  const specified =
    item.allowPriceOverride === true ? (input.specifiedCents ?? null) : null;
  const balanceAmount =
    item.allowPriceOverride === true ? specified : item.priceCents;
  if (
    type === ItemType.GIFT_CARD &&
    (!Number.isSafeInteger(balanceAmount) || (balanceAmount ?? 0) <= 0)
  )
    throw new Error("Enter an amount greater than zero for this balance.");
  if (specified != null) {
    if (!Number.isSafeInteger(specified) || specified < 0)
      throw new Error("Enter a valid nonnegative amount.");
    if (specified < (item.priceOverrideMin ?? 0))
      throw new Error("Amount is below the catalog minimum.");
    if (item.priceOverrideMax != null && specified > item.priceOverrideMax)
      throw new Error("Amount exceeds the catalog maximum.");
  }
  if (type === ItemType.GUEST_PRIVS && !input.membership)
    throw new Error("Choose a membership for these guest privileges.");
  if (type === ItemType.DONATION && !input.donation)
    throw new Error("Choose a donation fund and initiative.");
  return {
    itemInstance: { assignId: item.assignId },
    person: input.recipient,
    quantity: type === ItemType.PRODUCT ? input.quantity : 1,
    specifiedAmountCents: specified,
    parent: null,
    options: {
      cardNum: type === ItemType.CARD ? input.cardNumber.trim() || null : null,
      balanceNumber:
        type === ItemType.GIFT_CARD ? input.balanceNumber.trim() || null : null,
      membership: type === ItemType.GUEST_PRIVS ? input.membership : null,
      donation: type === ItemType.DONATION ? (input.donation ?? null) : null,
    },
    updateLine: input.updateLine ?? null,
  };
}
